import { useBundle } from '@amzn/react-arb-tools';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Text } from '@amzn/stencil-react-components/text';
import { Range } from '@amzn/stencil-react-components/utils';
import { FormWrapper } from '@amzn/stencil-react-components/form';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import {
  Col,
  GridContainer,
  GridItem,
} from '@amzn/stencil-react-components/layout';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from '../constants';
import { t } from '../helpers';
import { useCreateInterest } from 'src/hooks/useCreateInterest';

interface FieldConfig {
  name: string;
  text: string;
  placeholder: string;
  required: boolean;
}

const signUpFieldsConfig: FieldConfig[] = [
  {
    name: 'firstName',
    text: 'first_name_field',
    placeholder: 'first_name_field_placeholder',
    required: true,
  },
  {
    name: 'lastName',
    text: 'last_name_field',
    placeholder: 'last_name_field_placeholder',
    required: true,
  },
  {
    name: 'email',
    text: 'email_field',
    placeholder: 'email_field_placeholder',
    required: true,
  },
  {
    name: 'referral',
    text: 'referral_field',
    placeholder: 'referral_field_placeholder',
    required: false,
  },
];

export interface SignUpSectionProps {
  gridSize: Range<1, 13>;
}

const SignUpSection = (props: SignUpSectionProps) => {
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const { mutateAsync: createInterestMutation } =
    useCreateInterest('signup_section');

  const [signUpFields, setSignUpFields] = React.useState<Map<string, string>>(
    () => new Map(signUpFieldsConfig.map((config) => [config.name, ''])),
  );
  const [fieldTouched, setFieldTouched] = React.useState<Map<string, boolean>>(
    new Map(),
  );

  const updateAnswers = (fieldName: string, value: string): void => {
    const updatedFields = new Map(signUpFields);
    updatedFields.set(fieldName, value);
    setSignUpFields(updatedFields);

    updatedFieldsTouched(fieldName);
  };

  const updatedFieldsTouched = (fieldName: string): void => {
    const updatedFieldsTouched = new Map(fieldTouched);
    updatedFieldsTouched.set(fieldName, true);
    setFieldTouched(updatedFieldsTouched);
  };

  const [searchParams] = useSearchParams();
  const partner = searchParams.get('partner');

  return (
    <GridContainer dataTestId="sign-up-test-id">
      {signUpFieldsConfig.map((field) => (
        <GridItem key={field.name} lg={props.gridSize} md={8}>
          <FormWrapper width={'100%'}>
            <InputWrapper
              id={`${field.name}-id`}
              labelText={t(bundle, field.text) || field.text}
              required={field.required}
            >
              {(inputProps) => (
                <Input
                  {...inputProps}
                  dataTestId={`sign-up-input-${field.name}-test-id`}
                  name={field.name}
                  value={signUpFields.get(field.name)}
                  onChange={(event) => {
                    updateAnswers(field.name, event.target.value);
                  }}
                  placeholder={t(bundle, field.placeholder)}
                  onBlur={() => updatedFieldsTouched(field.name)}
                />
              )}
            </InputWrapper>
          </FormWrapper>
        </GridItem>
      ))}
      <GridItem lg={props.gridSize} md={8}>
        <Col width={'100%'}>
          <Button
            variant={ButtonVariant.Primary}
            dataTestId="submit-button-sign-up-test-id"
            onClick={() =>
              createInterestMutation({
                firstName: signUpFields.get('firstName')!,
                lastName: signUpFields.get('lastName')!,
                partner: partner,
                email: signUpFields.get('email')!,
                referral: signUpFields.get('referral')!,
              })
            }
          >
            <Text textAlign="center">
              {t(bundle, 'submit_interest_button')}
            </Text>
          </Button>
        </Col>
      </GridItem>
    </GridContainer>
  );
};

export default SignUpSection;
